// src/components/InvoicingPanel.jsx
// Platform admin: monthly invoicing (review and send). Shows what each tutor
// owes for last month, previews the PDF, sends it from
// invoices@homework-solutions.com and tracks payment. All the sums are done
// server-side by invoiceAction (functions/index.js), which applies the Order
// Form rules and records exactly what each invoice covered. AI summaries are
// no longer charged for (CHARGE_AI_SUMMARIES in functions/index.js).

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Receipt, RefreshCw, Eye, Send, Check, Settings, Download, Plus, Trash2, Link2 } from 'lucide-react';
import { invoiceAction, freeagentAction, getInvoiceSettings, saveInvoiceSettings, getAllOrgBilling, saveOrgBilling, getSentInvoices, getPendingExtras, addExtraCharge, removeExtraCharge } from '../firebase/platformAdmin';

const POUND = '\u00a3';
const money = (n) => `${POUND}${Number(n || 0).toFixed(2)}`;
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const SETTINGS_FIELDS = [
  ['businessName', 'Business name', 'A & S Edgar'],
  ['tradingName', 'Trading name', 'Homework Solutions'],
  ['address', 'Address (one line each)', '75 Ayr Road\nPrestwick\nKA9 1TF\nUnited Kingdom', true],
  ['email', 'Email shown on invoices', 'invoices@homework-solutions.com'],
  ['accountName', 'Bank account name', ''],
  ['bankName', 'Bank', ''],
  ['sortCode', 'Sort code', ''],
  ['accountNumber', 'Account number', ''],
  ['paymentNote', 'Extra payment note (optional)', ''],
  ['signOff', 'Name at the end of invoice emails', 'Stuart'],
  ['numberPrefix', 'Invoice number prefix', 'HS-']
];

const STATUS_STYLES = {
  due: { label: 'Due', bg: '#c6f6d5', color: '#22543d' },
  carry_over: { label: `Under ${POUND}50 - carry over`, bg: '#fefcbf', color: '#744210' },
  nothing: { label: 'Nothing to invoice', bg: '#edf2f7', color: '#718096' }
};

const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' };
const smallButton = (bg, color, border) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', background: bg, color, border: border || 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' });

function openPdf(base64) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function InvoicingPanel({ orgs }) {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [billing, setBilling] = useState({});
  const [drafts, setDrafts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [extras, setExtras] = useState([]);
  const [newCharge, setNewCharge] = useState({ orgId: '', description: '', hours: '', rate: '40' });
  const [freeagent, setFreeagent] = useState(null);
  const [freeagentContacts, setFreeagentContacts] = useState(null);
  const [choosingContactFor, setChoosingContactFor] = useState(null);

  const loadStored = useCallback(async () => {
    try {
      const [s, b, inv, ex] = await Promise.all([getInvoiceSettings(), getAllOrgBilling(), getSentInvoices(), getPendingExtras()]);
      setSettings(s); setBilling(b); setInvoices(inv); setExtras(ex);
    } catch (err) {
      console.error('Invoicing load error:', err);
      setError('Could not load invoicing data.');
    }
  }, []);

  const loadFreeagent = useCallback(async () => {
    try {
      setFreeagent(await freeagentAction({ action: 'status' }));
    } catch (err) {
      console.error('FreeAgent status error:', err);
      setFreeagent({ connected: false, statusError: err.message });
    }
  }, []);

  useEffect(() => { if (open) { loadStored(); loadFreeagent(); } }, [open, loadStored, loadFreeagent]);

  const bankDetailsMissing = !settings.sortCode || !settings.accountNumber;
  const freeagentConnected = !!freeagent?.connected;

  const handleFreeagentConnect = async () => {
    setBusy('freeagent-connect');
    try {
      const { url } = await freeagentAction({ action: 'connect' });
      window.open(url, '_blank');
    } catch (err) {
      alert('Could not start connecting to FreeAgent: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleFreeagentDisconnect = async () => {
    if (!window.confirm('Disconnect FreeAgent?\n\nNew invoices would go back to using the platform\'s own numbers and wouldn\'t be copied to FreeAgent. Nothing already in FreeAgent is changed.')) return;
    setBusy('freeagent-disconnect');
    try {
      await freeagentAction({ action: 'disconnect' });
      await loadFreeagent();
    } catch (err) {
      alert('Could not disconnect: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleFreeagentSync = async () => {
    setBusy('freeagent-sync');
    try {
      const r = await freeagentAction({ action: 'sync' });
      await Promise.all([loadStored(), loadFreeagent()]);
      alert(r.firstError ? `Checked ${r.checked} invoice(s), with a problem: ${r.firstError}` : `Checked ${r.checked} invoice(s). ${r.nowPaid} newly paid.`);
    } catch (err) {
      alert('Could not check FreeAgent: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const openContactChooser = async (orgId) => {
    setChoosingContactFor(orgId);
    setBusy('freeagent-contacts');
    try {
      const { contacts } = await freeagentAction({ action: 'listContacts' });
      setFreeagentContacts(contacts);
    } catch (err) {
      setChoosingContactFor(null);
      alert('Could not load FreeAgent contacts: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleLinkContact = async (orgId, contactUrl) => {
    if (!contactUrl) return;
    setBusy('freeagent-contacts');
    try {
      await freeagentAction({ action: 'linkContact', orgId, contactUrl });
      setChoosingContactFor(null);
      await loadStored();
    } catch (err) {
      alert('Could not link the contact: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleCreateContact = async (orgId) => {
    const name = orgName(orgId);
    if (!window.confirm(`Create a new FreeAgent contact for ${name}, using their saved billing details?`)) return;
    setBusy('freeagent-contacts');
    try {
      await freeagentAction({ action: 'createContact', orgId });
      setChoosingContactFor(null);
      await loadStored();
    } catch (err) {
      alert('Could not create the contact: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const checkDue = async () => {
    setChecking(true); setError('');
    try {
      const { drafts: d } = await invoiceAction({ action: 'summary' });
      setDrafts(d);
    } catch (err) {
      setError('Could not work out what is due: ' + err.message);
    } finally {
      setChecking(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const toSave = {};
      SETTINGS_FIELDS.forEach(([key]) => { if (settings[key] !== undefined) toSave[key] = settings[key]; });
      if (settings.nextNumber !== undefined && settings.nextNumber !== '') toSave.nextNumber = Number(settings.nextNumber);
      await saveInvoiceSettings(toSave);
      alert('Invoice settings saved.');
    } catch (err) {
      alert('Could not save: ' + err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const updateBilling = (orgId, key, value) => setBilling(prev => ({ ...prev, [orgId]: { ...(prev[orgId] || {}), [key]: value } }));
  const handleSaveBilling = async (orgId) => {
    try {
      const b = billing[orgId] || {};
      await saveOrgBilling(orgId, {
        billingEmail: (b.billingEmail || '').trim(),
        billingName: (b.billingName || '').trim(),
        billingAddress: b.billingAddress || '',
        pricePerStudent: Number(b.pricePerStudent ?? 4)
      });
      alert('Billing details saved.');
      if (drafts) checkDue();
    } catch (err) {
      alert('Could not save: ' + err.message);
    }
  };

  const handlePreview = async (orgId) => {
    setBusy(`preview-${orgId}`);
    try {
      const { pdfBase64 } = await invoiceAction({ action: 'preview', orgId });
      openPdf(pdfBase64);
    } catch (err) {
      alert('Could not preview: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleSend = async (draft) => {
    const to = billing[draft.orgId]?.billingEmail;
    if (!window.confirm(`Send an invoice for ${money(draft.totalGbp)} to ${draft.orgName} (${to})?\n\nIt's emailed from invoices@homework-solutions.com, with a copy to you, and the students on it won't be charged again.${freeagentConnected ? `\n\nIt's also created in FreeAgent, under ${billing[draft.orgId]?.freeagentContactName}, which gives it its number.` : ''}`)) return;
    setBusy(`send-${draft.orgId}`);
    try {
      const r = await invoiceAction({ action: 'send', orgId: draft.orgId });
      alert(`Invoice ${r.number} for ${money(r.totalGbp)} sent to ${r.sentTo}.${r.freeagentWarning ? `\n\n${r.freeagentWarning}` : ''}`);
      await Promise.all([loadStored(), checkDue()]);
    } catch (err) {
      alert('Could not send: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleInvoicePdf = async (inv) => {
    setBusy(`pdf-${inv.id}`);
    try {
      const { pdfBase64 } = await invoiceAction({ action: 'pdf', invoiceId: inv.id });
      openPdf(pdfBase64);
    } catch (err) {
      alert('Could not open the invoice: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleTogglePaid = async (inv) => {
    try {
      await invoiceAction({ action: inv.status === 'paid' ? 'markUnpaid' : 'markPaid', invoiceId: inv.id });
      await loadStored();
    } catch (err) {
      alert('Could not update: ' + err.message);
    }
  };

  const handleAddCharge = async () => {
    const hours = Number(newCharge.hours), rate = Number(newCharge.rate);
    if (!newCharge.orgId || !newCharge.description.trim() || !(hours > 0) || !(rate >= 0)) {
      alert('Choose a tutor and enter a description, the hours and the rate.');
      return;
    }
    try {
      await addExtraCharge({ orgId: newCharge.orgId, description: newCharge.description.trim(), hours, rate });
      setNewCharge({ orgId: newCharge.orgId, description: '', hours: '', rate: newCharge.rate });
      await loadStored();
      if (drafts) checkDue();
    } catch (err) {
      alert('Could not add the charge: ' + err.message);
    }
  };

  const handleRemoveCharge = async (charge) => {
    if (!window.confirm(`Remove "${charge.description}" (${money(charge.hours * charge.rate)}) for ${orgName(charge.orgId)}?`)) return;
    try {
      await removeExtraCharge(charge.id);
      await loadStored();
      if (drafts) checkDue();
    } catch (err) {
      alert('Could not remove: ' + err.message);
    }
  };

  const orgName = (orgId) => orgs.find(o => o.orgId === orgId)?.name || orgId;
  const headMarkerEmail = (orgId) => orgs.find(o => o.orgId === orgId)?.headMarkers?.[0]?.email || '';
  const closedInFreeagent = (inv) => ['Refunded', 'Written-off', 'Part written-off', 'Cancelled'].includes(inv.freeagentStatus);
  const overdue = (inv) => inv.status !== 'paid' && !closedInFreeagent(inv) && inv.dueDateIso && new Date(inv.dueDateIso) < new Date();
  const paidByFreeagent = (inv) => !!inv.freeagentInvoiceUrl && freeagentConnected;

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '16px' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#2d3748', width: '100%', textAlign: 'left' }}
      >
        {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        <h2 style={{ margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><Receipt size={18} /> Invoicing</h2>
        {invoices.some(overdue) && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#fed7d7', color: '#9b2c2c', borderRadius: '10px', fontSize: '12px', fontWeight: 700 }}>Overdue invoices</span>}
      </button>

      {open && (
        <div style={{ marginTop: '16px', fontSize: '13px', color: '#4a5568' }}>
          {error && <div style={{ color: '#c53030', marginBottom: '10px' }}>{error}</div>}

          {/* FreeAgent */}
          {freeagent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '10px 12px', background: '#f7fafc', borderRadius: '8px', marginBottom: '12px' }}>
              <Link2 size={15} />
              <strong style={{ color: '#2d3748' }}>FreeAgent{freeagent.sandbox ? ' sandbox' : ''}:</strong>
              {freeagentConnected ? (
                <>
                  <span>Connected to {freeagent.companyName || 'your account'}</span>
                  {freeagent.lastSyncAtMs && <span style={{ color: '#a0aec0' }}>Payments last checked {shortDate(new Date(freeagent.lastSyncAtMs).toISOString())}</span>}
                  {freeagent.lastSyncError && <span style={{ color: '#c53030' }}>{freeagent.lastSyncError}</span>}
                  <button onClick={handleFreeagentSync} disabled={!!busy} style={{ ...smallButton('none', '#2b6cb0', '1px solid #2b6cb0'), marginLeft: 'auto' }}>
                    <RefreshCw size={12} /> {busy === 'freeagent-sync' ? 'Checking...' : 'Check payments now'}
                  </button>
                  <button onClick={handleFreeagentDisconnect} disabled={!!busy} style={smallButton('none', '#4a5568', '1px solid #cbd5e0')}>Disconnect</button>
                </>
              ) : (
                <>
                  <span>{freeagent.statusError ? `Could not check the connection: ${freeagent.statusError}` : 'Not connected'}</span>
                  <button onClick={handleFreeagentConnect} disabled={!!busy} style={{ ...smallButton('#2b6cb0', 'white'), marginLeft: 'auto' }}>
                    {busy === 'freeagent-connect' ? 'Opening...' : 'Connect'}
                  </button>
                  <button onClick={loadFreeagent} disabled={!!busy} style={smallButton('none', '#4a5568', '1px solid #cbd5e0')}>I've approved it, refresh</button>
                </>
              )}
            </div>
          )}

          {/* Settings */}
          <button onClick={() => setShowSettings(!showSettings)} style={smallButton('none', '#2b6cb0', '1px solid #2b6cb0')}>
            <Settings size={13} /> Invoice settings {bankDetailsMissing && <span style={{ color: '#c53030' }}>(bank details needed)</span>}
          </button>
          {showSettings && (
            <div style={{ margin: '12px 0', padding: '14px', background: '#f7fafc', borderRadius: '8px', display: 'grid', gap: '10px', maxWidth: '520px' }}>
              {SETTINGS_FIELDS.filter(([key]) => !(freeagentConnected && key === 'numberPrefix')).map(([key, label, placeholder, multiline]) => (
                <label key={key} style={{ display: 'grid', gap: '4px' }}>
                  <span style={{ fontWeight: 600 }}>{label}</span>
                  {multiline ? (
                    <textarea rows={4} value={settings[key] ?? ''} placeholder={placeholder} onChange={e => setSettings({ ...settings, [key]: e.target.value })} style={inputStyle} />
                  ) : (
                    <input value={settings[key] ?? ''} placeholder={placeholder} onChange={e => setSettings({ ...settings, [key]: e.target.value })} style={inputStyle} />
                  )}
                </label>
              ))}
              {freeagentConnected ? (
                <div style={{ fontSize: '12px', color: '#718096' }}>Invoice numbers come from FreeAgent while it's connected.</div>
              ) : (
                <label style={{ display: 'grid', gap: '4px' }}>
                  <span style={{ fontWeight: 600 }}>Next invoice number</span>
                  <input type="number" min="1" value={settings.nextNumber ?? ''} placeholder="1" onChange={e => setSettings({ ...settings, nextNumber: e.target.value })} style={inputStyle} />
                  <span style={{ fontSize: '11px', color: '#a0aec0' }}>Counts up automatically. Only change it if you need to continue from an existing number.</span>
                </label>
              )}
              <div style={{ fontSize: '11px', color: '#a0aec0' }}>Only your platform admin account can see these. Leave a field blank to use the grey example.</div>
              <button onClick={handleSaveSettings} disabled={savingSettings} style={smallButton('#2b6cb0', 'white')}>{savingSettings ? 'Saving...' : 'Save settings'}</button>
            </div>
          )}

          {/* Additional charges */}
          <strong style={{ display: 'block', fontSize: '15px', color: '#2d3748', margin: '16px 0 8px' }}>Additional charges</strong>
          <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '8px' }}>
            Set-up work and other extras. Each one goes on the tutor's next invoice, then never again.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1.2fr) minmax(180px, 2fr) 90px 90px auto', gap: '8px', alignItems: 'center' }}>
            <select value={newCharge.orgId} onChange={e => setNewCharge({ ...newCharge, orgId: e.target.value })} style={inputStyle}>
              <option value="">Tutor...</option>
              {orgs.map(o => <option key={o.orgId} value={o.orgId}>{o.name}</option>)}
            </select>
            <input value={newCharge.description} placeholder="e.g. Setting up RH5 homework" onChange={e => setNewCharge({ ...newCharge, description: e.target.value })} style={inputStyle} />
            <input type="number" min="0" step="0.25" value={newCharge.hours} placeholder="Hours" onChange={e => setNewCharge({ ...newCharge, hours: e.target.value })} style={inputStyle} />
            <input type="number" min="0" step="1" value={newCharge.rate} title={`Rate per hour (${POUND})`} onChange={e => setNewCharge({ ...newCharge, rate: e.target.value })} style={inputStyle} />
            <button onClick={handleAddCharge} style={smallButton('#2b6cb0', 'white')}><Plus size={13} /> Add</button>
          </div>
          {newCharge.hours > 0 && newCharge.rate !== '' && (
            <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>= {money(Number(newCharge.hours) * Number(newCharge.rate))}</div>
          )}
          {extras.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {extras.map(x => (
                <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0', borderTop: '1px solid #edf2f7' }}>
                  <span style={{ flex: 1 }}>
                    <strong>{orgName(x.orgId)}</strong>: {x.description} ({x.hours} hour{Number(x.hours) === 1 ? '' : 's'} x {money(x.rate)}) = {money(x.hours * x.rate)}
                    <span style={{ color: '#a0aec0' }}> - waiting for next invoice</span>
                  </span>
                  <button onClick={() => handleRemoveCharge(x)} style={smallButton('none', '#e53e3e', '1px solid #feb2b2')}><Trash2 size={12} /> Remove</button>
                </div>
              ))}
            </div>
          )}

          {/* Due */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 8px' }}>
            <strong style={{ fontSize: '15px', color: '#2d3748' }}>Invoices due</strong>
            <button onClick={checkDue} disabled={checking} style={smallButton('#2b6cb0', 'white')}>
              <RefreshCw size={13} /> {checking ? 'Working it out...' : drafts ? 'Check again' : "Check what's due"}
            </button>
          </div>
          <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '8px' }}>
            Students who became chargeable before the 1st of this month, not yet invoiced. Best checked on the 1st.
          </div>
          {drafts && drafts.length === 0 && <div style={{ color: '#a0aec0' }}>No tutors with anything to bill yet.</div>}
          {drafts && drafts.map(d => {
            const st = STATUS_STYLES[d.status];
            const b = billing[d.orgId] || {};
            return (
              <div key={d.orgId} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <strong style={{ color: '#2d3748' }}>{d.orgName}</strong>
                  <span style={{ padding: '2px 8px', background: st.bg, color: st.color, borderRadius: '10px', fontSize: '11px', fontWeight: 700 }}>{st.label}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#2d3748' }}>{money(d.totalGbp)}</span>
                </div>
                <div style={{ marginTop: '6px' }}>
                  {d.studentCount} student{d.studentCount === 1 ? '' : 's'} x {money(d.pricePerStudent)} = {money(d.studentsGbp)}
                  {d.cohorts.length > 0 && <span style={{ color: '#a0aec0' }}> ({d.cohorts.map(c => `${c.cohortName}: ${c.count}`).join(', ')})</span>}
                </div>
                {d.aiMonths.length > 0 && (
                  <div>
                    AI summaries ({d.aiMonths.map(m => m.month).join(', ')}): ${d.aiUsd.toFixed(4)}
                    {d.rate ? ` = ${money(d.aiGbp)} at ${d.rate.toFixed(4)}` : <span style={{ color: '#c53030' }}> (exchange rate unavailable: {d.rateError})</span>}
                  </div>
                )}
                {d.extras && d.extras.length > 0 && (
                  <div>Set-up work: {d.extras.map(x => `${x.description} (${money(x.amountGbp)})`).join(', ')} = {money(d.extrasGbp)}</div>
                )}
                {d.lastInvoiceAt && <div style={{ color: '#a0aec0' }}>Last invoiced {shortDate(d.lastInvoiceAt)}</div>}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', marginTop: '10px' }}>
                  <input value={b.billingEmail ?? ''} placeholder={headMarkerEmail(d.orgId) || 'Billing email'} onChange={e => updateBilling(d.orgId, 'billingEmail', e.target.value)} style={inputStyle} title="Billing email" />
                  <input value={b.billingName ?? ''} placeholder="Billing contact name" onChange={e => updateBilling(d.orgId, 'billingName', e.target.value)} style={inputStyle} />
                  <input value={b.billingAddress ?? ''} placeholder="Billing address (from Order Form)" onChange={e => updateBilling(d.orgId, 'billingAddress', e.target.value)} style={inputStyle} />
                  <input type="number" step="0.5" min="0" value={b.pricePerStudent ?? 4} onChange={e => updateBilling(d.orgId, 'pricePerStudent', e.target.value)} style={inputStyle} title={`Price per student (${POUND})`} />
                </div>
                {freeagentConnected && (
                  <div style={{ marginTop: '10px', padding: '8px 10px', background: '#f7fafc', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <strong>FreeAgent contact:</strong>
                      {b.freeagentContactUrl ? <span>{b.freeagentContactName}</span> : <span style={{ color: '#c53030' }}>Not chosen yet</span>}
                      <button onClick={() => openContactChooser(d.orgId)} disabled={!!busy} style={smallButton('none', '#2b6cb0', '1px solid #2b6cb0')}>
                        {b.freeagentContactUrl ? 'Change' : 'Choose contact'}
                      </button>
                    </div>
                    {choosingContactFor === d.orgId && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {busy === 'freeagent-contacts' || !freeagentContacts ? <span>Working...</span> : (
                          <>
                            <select defaultValue="" onChange={e => handleLinkContact(d.orgId, e.target.value)} style={{ ...inputStyle, width: 'auto', maxWidth: '360px' }}>
                              <option value="">Choose an existing contact...</option>
                              {[...freeagentContacts]
                                .sort((x, y) => Number(y.email.toLowerCase() === (b.billingEmail || '').toLowerCase() && !!y.email) - Number(x.email.toLowerCase() === (b.billingEmail || '').toLowerCase() && !!x.email))
                                .map(c => <option key={c.url} value={c.url}>{c.name}{c.email ? ` (${c.email})` : ''}{c.email && c.email.toLowerCase() === (b.billingEmail || '').toLowerCase() ? ' - same billing email' : ''}</option>)}
                            </select>
                            <button onClick={() => handleCreateContact(d.orgId)} style={smallButton('none', '#2b6cb0', '1px solid #2b6cb0')}><Plus size={12} /> Create new contact</button>
                            <button onClick={() => setChoosingContactFor(null)} style={smallButton('none', '#4a5568', '1px solid #cbd5e0')}>Cancel</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => handleSaveBilling(d.orgId)} style={smallButton('none', '#4a5568', '1px solid #cbd5e0')}>Save billing details</button>
                  {!b.billingEmail && headMarkerEmail(d.orgId) && (
                    <button onClick={() => updateBilling(d.orgId, 'billingEmail', headMarkerEmail(d.orgId))} style={smallButton('none', '#4a5568', '1px solid #cbd5e0')}>Use {headMarkerEmail(d.orgId)}</button>
                  )}
                  <button onClick={() => handlePreview(d.orgId)} disabled={d.totalGbp <= 0 || busy} style={smallButton('none', '#2b6cb0', '1px solid #2b6cb0')}>
                    <Eye size={13} /> {busy === `preview-${d.orgId}` ? 'Preparing...' : 'Preview PDF'}
                  </button>
                  <button
                    onClick={() => handleSend(d)}
                    disabled={d.totalGbp <= 0 || busy || !freeagent || !b.billingEmail || bankDetailsMissing || (freeagentConnected && !b.freeagentContactUrl)}
                    title={!b.billingEmail ? 'Save a billing email first' : bankDetailsMissing ? 'Add your bank details in Invoice settings first' : freeagentConnected && !b.freeagentContactUrl ? 'Choose the FreeAgent contact first' : ''}
                    style={smallButton(d.totalGbp <= 0 || !b.billingEmail || bankDetailsMissing || (freeagentConnected && !b.freeagentContactUrl) ? '#e2e8f0' : '#1b998b', d.totalGbp <= 0 || !b.billingEmail || bankDetailsMissing || (freeagentConnected && !b.freeagentContactUrl) ? '#a0aec0' : 'white')}
                  >
                    <Send size={13} /> {busy === `send-${d.orgId}` ? 'Sending...' : d.status === 'carry_over' ? 'Send anyway' : 'Send invoice'}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Sent */}
          <strong style={{ display: 'block', fontSize: '15px', color: '#2d3748', margin: '18px 0 8px' }}>Sent invoices</strong>
          {invoices.length === 0 ? (
            <div style={{ color: '#a0aec0' }}>None yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#718096' }}>
                  <th style={{ padding: '4px 6px' }}>Invoice</th><th style={{ padding: '4px 6px' }}>Tutor</th><th style={{ padding: '4px 6px' }}>Sent</th>
                  <th style={{ padding: '4px 6px' }}>Total</th><th style={{ padding: '4px 6px' }}>Due</th><th style={{ padding: '4px 6px' }}>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} style={{ borderTop: '1px solid #edf2f7' }}>
                    <td style={{ padding: '6px' }}><strong>{inv.number}</strong></td>
                    <td style={{ padding: '6px' }}>{inv.orgName || orgName(inv.orgId)}</td>
                    <td style={{ padding: '6px' }}>{shortDate(inv.invoicedAtIso)}</td>
                    <td style={{ padding: '6px' }}>{money(inv.totalGbp)}</td>
                    <td style={{ padding: '6px' }}>{shortDate(inv.dueDateIso)}</td>
                    <td style={{ padding: '6px' }}>
                      {inv.status === 'paid'
                        ? <span style={{ color: '#22543d', fontWeight: 700 }}>Paid</span>
                        : closedInFreeagent(inv)
                          ? <span>{inv.freeagentStatus}</span>
                          : (
                            <>
                              {overdue(inv) ? <span style={{ color: '#9b2c2c', fontWeight: 700 }}>Overdue</span> : <span>Sent</span>}
                              {inv.freeagentPaidValue > 0 && <div style={{ fontSize: '11px', color: '#718096' }}>{money(inv.freeagentPaidValue)} paid so far</div>}
                            </>
                          )}
                      {inv.freeagentMarkSentPending && <div style={{ fontSize: '11px', color: '#c53030' }}>Still a draft in FreeAgent</div>}
                      {inv.freeagentError && !inv.freeagentMarkSentPending && <div style={{ fontSize: '11px', color: '#c53030' }}>{inv.freeagentError}</div>}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleInvoicePdf(inv)} disabled={!!busy} style={{ ...smallButton('none', '#2b6cb0', '1px solid #cbd5e0'), marginRight: '6px' }}><Download size={12} /> PDF</button>
                      {paidByFreeagent(inv) ? (
                        <span style={{ fontSize: '11px', color: '#a0aec0' }}>Payment tracked in FreeAgent</span>
                      ) : (
                        <button onClick={() => handleTogglePaid(inv)} style={smallButton(inv.status === 'paid' ? 'none' : '#1b998b', inv.status === 'paid' ? '#4a5568' : 'white', inv.status === 'paid' ? '1px solid #cbd5e0' : undefined)}>
                          {inv.status === 'paid' ? 'Mark unpaid' : <><Check size={12} /> Mark paid</>}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default InvoicingPanel;
